import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Search, Plus, Check, X, ExternalLink, RefreshCw,
  Layers, Globe, ChevronRight, Trash2, Activity,
  TrendingUp, AlertCircle, Copy, ClipboardPaste
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXCHANGE_ICONS = {
  pump_fun:    { letter: 'P', gradient: 'from-[#00D18C] to-[#00A86B]' },
  raydium:     { letter: 'R', gradient: 'from-[#2B6DEF] to-[#1A4FBF]' },
  jupiter:     { letter: 'J', gradient: 'from-[#19FB9B] to-[#0EC880]' },
  uniswap:     { letter: 'U', gradient: 'from-[#FF007A] to-[#CC0062]' },
  pancakeswap: { letter: 'C', gradient: 'from-[#D1884F] to-[#A66A3F]' },
};

const CHAIN_LABELS = {
  solana: { name: 'Solana',    color: 'bg-[#AB9FF2]/10 text-[#C9440C]' },
  evm:    { name: 'Ethereum',  color: 'bg-[#627EEA]/10 text-[#627EEA]' },
  bsc:    { name: 'BNB Chain', color: 'bg-[#F0B90B]/10 text-[#B88D08]' },
};

export default function DefiExchanges() {
  const { panicMode } = useOutletContext();
  const [exchanges, setExchanges]           = useState([]);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [contractInput, setContractInput]   = useState('');
  const [symbolInput, setSymbolInput]       = useState('');
  const [adding, setAdding]                 = useState(false);
  const [myTokens, setMyTokens]             = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);

  const fetchExchanges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges`);
      setExchanges(data.exchanges || []);
    } catch { /* silent */ }
    finally { setLoadingExchanges(false); }
  }, []);

  const fetchMyTokens = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/selected-tokens`);
      setMyTokens(data.tokens || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchExchanges();
    fetchMyTokens();
  }, [fetchExchanges, fetchMyTokens]);

  const handleAddContract = async () => {
    if (!contractInput.trim()) {
      toast.error('Paste a contract address first');
      return;
    }
    if (!selectedExchange) {
      toast.error('Select an exchange first');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/defi/select-token`, {
        token_symbol: symbolInput.trim().toUpperCase() || contractInput.slice(0, 8),
        token_address: contractInput.trim(),
        exchange_id: selectedExchange.id,
      });
      toast.success('Contract added to your bot pool');
      setContractInput('');
      setSymbolInput('');
      fetchMyTokens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add contract');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (token) => {
    try {
      await axios.delete(`${API_URL}/api/defi/selected-tokens/${token.exchange_id}/${token.symbol}`);
      toast.success(`${token.symbol} removed`);
      fetchMyTokens();
    } catch { toast.error('Failed to remove'); }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setContractInput(text.trim());
    } catch {
      toast.error('Clipboard access denied — paste manually');
    }
  };

  return (
    <div className="space-y-6" data-testid="defi-exchanges-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Layers className="w-7 h-7 text-[#E8520F]" /> DeFi Exchanges
          </h1>
          <p className="text-[hsl(215,16%,56%)]">Select an exchange and paste a contract address to add tokens to your bots</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchExchanges(); fetchMyTokens(); }}
          className="border-[hsl(220,16%,18%)]">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Legal notice */}
      <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-300">
          Contract addresses are not provided by GauriLabs. You are responsible for verifying any contract before use. Always DYOR.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Exchange List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-[hsl(215,16%,56%)] uppercase tracking-wide px-1">1. Select Exchange</h2>
          {loadingExchanges ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-[#E8520F] animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {exchanges.map((exchange) => {
                const icon = EXCHANGE_ICONS[exchange.id] || { letter: exchange.name[0], gradient: 'from-[#E8520F] to-[#C9440C]' };
                const chainInfo = CHAIN_LABELS[exchange.chain] || CHAIN_LABELS.solana;
                const isActive = selectedExchange?.id === exchange.id;
                return (
                  <button key={exchange.id} onClick={() => { setSelectedExchange(exchange); setContractInput(''); setSymbolInput(''); }}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      isActive ? 'border-[#E8520F] bg-[#E8520F]/[0.04]' : 'border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] hover:border-[#CBD5E1]'
                    }`} data-testid={`exchange-card-${exchange.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${icon.gradient} flex items-center justify-center shrink-0`}>
                        <span className="text-white font-bold text-sm">{icon.letter}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-white text-sm">{exchange.name}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${chainInfo.color}`}>{chainInfo.name}</span>
                        </div>
                        <p className="text-xs text-[hsl(215,16%,56%)] truncate">{exchange.description}</p>
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${isActive ? 'text-[#E8520F]' : 'text-[#CBD5E1]'}`} />
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[hsl(220,16%,18%)]">
                      {exchange.website && (
                        <a href={exchange.website} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="flex items-center gap-1 text-xs text-[#E8520F] hover:underline">
                          <Globe className="w-3 h-3" /> Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Contract Input Panel */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-sm font-semibold text-[hsl(215,16%,56%)] uppercase tracking-wide px-1">2. Add Contract Address</h2>

          {selectedExchange ? (
            <Card className="border-[hsl(220,16%,18%)] shadow-none">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${EXCHANGE_ICONS[selectedExchange.id]?.gradient || 'from-[#E8520F] to-[#C9440C]'} flex items-center justify-center`}>
                    <span className="text-white font-bold text-xs">{EXCHANGE_ICONS[selectedExchange.id]?.letter}</span>
                  </div>
                  <div>
                    <CardTitle className="text-white font-['Outfit'] text-base">{selectedExchange.name}</CardTitle>
                    <p className="text-xs text-[hsl(215,16%,56%)]">Paste the contract address of the token you want to trade</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Token Symbol */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[hsl(215,16%,56%)] uppercase tracking-wide">Token Symbol (optional)</label>
                  <Input
                    value={symbolInput}
                    onChange={e => setSymbolInput(e.target.value)}
                    placeholder="e.g. BONK, WIF, MYTOKEN"
                    className="border-[hsl(220,16%,18%)] bg-[hsl(220,16%,14%)] text-white uppercase"
                    maxLength={10}
                  />
                  <p className="text-[10px] text-[hsl(215,16%,40%)]">If left blank, first 8 chars of contract address will be used</p>
                </div>

                {/* Contract Address */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[hsl(215,16%,56%)] uppercase tracking-wide">Contract Address *</label>
                  <div className="flex gap-2">
                    <Input
                      value={contractInput}
                      onChange={e => setContractInput(e.target.value)}
                      placeholder={selectedExchange.chain === 'solana' ? 'Solana token mint address (e.g. DezX...)' : 'EVM contract address (e.g. 0x...)'}
                      className="border-[hsl(220,16%,18%)] bg-[hsl(220,16%,14%)] text-white font-mono text-xs"
                    />
                    <Button variant="outline" size="sm" onClick={handlePaste}
                      className="border-[hsl(220,16%,18%)] text-[hsl(215,16%,56%)] hover:text-white shrink-0 px-3">
                      <ClipboardPaste className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-[hsl(215,16%,40%)]">
                    You are responsible for verifying this contract. GauriLabs does not validate or endorse any token.
                  </p>
                </div>

                <Button onClick={handleAddContract} disabled={adding || !contractInput.trim() || panicMode}
                  className="w-full bg-[#E8520F] hover:bg-[#C9440C] text-white">
                  {adding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add to Bot Pool
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-[hsl(215,16%,56%)] border border-[hsl(220,16%,18%)] rounded-lg">
              <div className="w-16 h-16 rounded-2xl bg-[#E8520F]/10 flex items-center justify-center mb-4">
                <Layers className="w-8 h-8 text-[#E8520F]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Select an Exchange</h3>
              <p className="text-sm text-center max-w-sm">Choose a DeFi exchange from the left, then paste a contract address to add it to your bot trading pool.</p>
            </div>
          )}

          {/* My Added Contracts */}
          {myTokens.length > 0 && (
            <Card className="border-[hsl(220,16%,18%)] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#10B981]" />
                  My Bot Contracts ({myTokens.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {myTokens.map((token) => (
                    <div key={`${token.exchange_id}-${token.symbol}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)] group">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm text-white">{token.symbol}</span>
                          <span className="text-[10px] text-[hsl(215,16%,45%)]">on {token.exchange_name}</span>
                        </div>
                        {token.address && (
                          <p className="text-[10px] font-mono text-[hsl(215,16%,40%)] truncate mt-0.5">{token.address}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {token.address && (
                          <button onClick={() => { navigator.clipboard.writeText(token.address); toast.success('Copied!'); }}
                            className="p-1.5 rounded hover:bg-[hsl(220,16%,18%)] text-[hsl(215,16%,45%)] hover:text-white">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleRemove(token)}
                          className="p-1.5 rounded hover:bg-red-500/10 text-[hsl(215,16%,45%)] hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
